import React, { useEffect, useState } from 'react'
import {
	Box,
	Chip,
	CircularProgress,
	Paper,
	Tooltip,
	Typography,
} from '@mui/material'
import { AppCardTitle, AppEmptyLine, AppResultsSkeleton, AppNote } from '@components'
import { displayDay } from '@utils'
import { BarChart }  from '@mui/x-charts/BarChart'
import { LineChart } from '@mui/x-charts/LineChart'

import { buildTickFormatter, pickTickNumber } from './dateFormat'
import { dsColorNew, dataColors } from '@palette'
import { mbLegendBottom } from 'Pages/MyBusiness/_shared/Ui'

/**
 * Cancellation Rate widget — full-width Revenue companion.
 *
 * Pulls /api/my-business/cancellation-rate which returns both the
 * aggregate (cancelled / total / rate_pct) and a per-period series
 * matching the rest of the dashboard's granularity. PY comparison
 * shows a delta-pp chip plus a dashed line overlay on the chart.
 *
 *   <CancellationRateCard from="2026-01-01" to="2026-06-11"
 *                         offices={['A1']} categories={['ECMR']}
 *                         compare="py"/>
 */
const fmtNum = (n) => new Intl.NumberFormat('es-ES').format(n)
const fmtPct = (n) =>
	new Intl.NumberFormat('es-ES', { minimumFractionDigits: 1, maximumFractionDigits: 1 })
		.format(n) + '%'

export default function CancellationRateCard({
	from, to, offices = [], categories = [], compare = 'py', duration = 'total',
}) {
	const [data, setData] = useState(null)
	const [error, setError] = useState(null)
	const [loading, setLoading] = useState(true)

	useEffect(() => {
		if (!from || !to) return
		setLoading(true); setError(null)
		const qs = new URLSearchParams({ from, to })
		if (offices.length)    qs.set('offices', offices.join(','))
		if (categories.length) qs.set('sipps',   categories.join(','))
		if (compare && compare !== 'none') qs.set('compare', compare)
		if (duration && duration !== 'total') qs.set('duration', duration)
		fetch(`/api/my-business/cancellation-rate?${qs}`, { credentials: 'include' })
			.then(r => r.ok ? r.json() : Promise.reject(new Error(`HTTP ${r.status}`)))
			.then(setData)
			.catch(e => setError(e.message))
			.finally(() => setLoading(false))
	}, [from, to, offices.join(','), categories.join(','), compare, duration])

	if (error) return (
		<Paper sx={{ p: 2 }}>
			<AppNote severity="error">Cancellation rate unreachable: {error}</AppNote>
		</Paper>
	)
	// Loading (also a refetch): the card's skeleton, so nothing can be clicked half-loaded
	if (loading || !data) return <AppResultsSkeleton chart height={360}/>

	const rate = data.rate_pct
	const cmp = data.compare
	const delta = cmp ? cmp.delta_pp : null
	// Higher cancellation rate is BAD — invert the delta colour mapping.
	const deltaColor = delta == null ? 'default'
		: delta > 0 ? 'error'
		: delta < 0 ? 'success'
		: 'default'
	const sign = delta == null ? '' : (delta > 0 ? '+' : '')

	const rateBg =
		rate < 10 ? dataColors.green100 :
		rate < 15 ? dataColors.green200 :
		rate < 20 ? dsColorNew.noteAmberBorder :
		rate < 25 ? dataColors.orange300 :
		rate < 30 ? dsColorNew.dangerSoft :
		dsColorNew.dangerRed
	const rateColor = rate >= 20 ? dsColorNew.white : dataColors.slate800

	const series = data.series || []
	const cmpSeries = cmp?.series || []
	const periods = series.map(p => p.period)
	const rateValues = series.map(p => Number(p.rate_pct) || 0)
	const cmpByPeriod = new Map(cmpSeries.map(p => [p.period, Number(p.rate_pct) || 0]))
	const hasCmpData = cmpSeries.length > 0
	// MUI X Charts (v6.19.x in this codebase) chokes on null gaps inside
	// series data for BarChart and on tooltip readouts when the series
	// list is shorter than xAxis data. Substitute missing PY points with
	// 0 instead — that period simply renders as a flat baseline rather
	// than throwing.
	const cmpAligned = hasCmpData
		? periods.map(p => (cmpByPeriod.has(p) ? cmpByPeriod.get(p) : 0))
		: null

	const tickFormat = buildTickFormatter(periods)
	const tickNumber = pickTickNumber(periods)
	const granularity = data.granularity || 'monthly'
	const granLabel = granularity[0].toUpperCase() + granularity.slice(1)
	const useBar = series.length <= 31    // daily → bars, otherwise lines

	return (
		<Paper sx={{ p: 2.5 }}>
			{/* Header — KPI tile on the left, chart on the right of header text */}
			<Box display="flex" alignItems="flex-start" justifyContent="space-between"
			     flexWrap="wrap" gap={2} sx={{ mb: 1.5 }}>
				<Box display="flex" alignItems="center" gap={2}>
					<Box sx={{
						bgcolor: rateBg, color: rateColor,
						px: 2, py: 1.25, borderRadius: '12px',
						fontSize: 26, fontWeight: 700, lineHeight: 1,
						minWidth: 130, textAlign: 'center',
					}}>
						{fmtPct(rate)}
					</Box>
					<Box>
						<AppCardTitle icon="ban" title="Cancellation Rate" sx={{ minHeight: 0 }}
						              subtitle={`${displayDay(data.from)} → ${displayDay(data.to)} · ${granLabel}`}/>
						<Typography sx={{ fontSize: 14, color: dsColorNew.textDark, mt: 0.5 }}>
							<Box component="span" sx={{ fontWeight: 700 }}>{fmtNum(data.cancelled)}</Box>
							<Box component="span" sx={{ color: dsColorNew.textMuted }}>
								{' '}cancelled of{' '}
							</Box>
							<Box component="span" sx={{ fontWeight: 700 }}>{fmtNum(data.total)}</Box>
							<Box component="span" sx={{ color: dsColorNew.textMuted }}> bookings</Box>
						</Typography>
					</Box>
				</Box>
				{cmp && (
					<Box display="flex" alignItems="center" gap={1.25}>
						<Tooltip arrow placement="top"
						         title="Difference in percentage points vs the comparison window. Lower is better.">
							<Chip size="small"
							      variant="filled"
							      color={deltaColor}
							      label={delta == null ? '—' : `${sign}${delta.toFixed(1)} pp`}
							      sx={{ fontWeight: 600, fontSize: 13, height: 28,
							            cursor: 'help', px: 0.5 }}/>
						</Tooltip>
						<Box>
							<Typography sx={{ fontSize: 14, color: dsColorNew.textDark, lineHeight: 1.2 }}>
								vs {fmtPct(cmp.rate_pct)}
							</Typography>
							<Typography sx={{ fontSize: 12, color: dsColorNew.textMuted }}>
								same window 1y ago
							</Typography>
						</Box>
					</Box>
				)}
			</Box>

			{/* Time-series chart */}
			{series.length > 0 ? (
				<Box>
					{useBar ? (
						<BarChart
							xAxis={[{ scaleType: 'band', data: periods,
							          valueFormatter: tickFormat, tickNumber }]}
							series={[
								{
									data: rateValues, label: 'Cancellation %',
									color: dsColorNew.dangerRed,
									valueFormatter: (v) => v == null ? '—' : fmtPct(v),
								},
								...(hasCmpData ? [{
									data: cmpAligned, label: 'PY',
									color: dsColorNew.textSubtle,
									valueFormatter: (v) => v == null ? '—' : fmtPct(v),
								}] : []),
							]}
							height={260}
							slotProps={mbLegendBottom}
							margin={{ top: 16, right: 16, bottom: 72, left: 56 }}/>
					) : (
						<LineChart
							xAxis={[{ scaleType: 'point', data: periods,
							          valueFormatter: tickFormat, tickNumber }]}
							series={[
								...(hasCmpData ? [{
									data: cmpAligned, label: 'PY',
									color: dsColorNew.textSubtle, id: 'py',
									showMark: false, connectNulls: true,
									curve: 'monotoneX',
									valueFormatter: (v) => v == null ? '—' : fmtPct(v),
								}] : []),
								{
									data: rateValues, label: 'Cancellation %',
									color: dsColorNew.dangerRed, id: 'cur',
									showMark: false, area: true,
									curve: 'monotoneX',
									valueFormatter: (v) => v == null ? '—' : fmtPct(v),
								},
							]}
							sx={{
								'& .MuiLineElement-series-py': {
									strokeDasharray: '6 4',
									strokeWidth: 2,
								},
								'& .MuiAreaElement-series-cur': { fillOpacity: 0.18 },
							}}
							height={260}
							slotProps={mbLegendBottom}
							margin={{ top: 16, right: 16, bottom: 72, left: 56 }}/>
					)}
				</Box>
			) : (
				<AppEmptyLine>No bookings in this window.</AppEmptyLine>
			)}

			<Typography sx={{ fontSize: 12, color: dsColorNew.textMuted, mt: 0.5 }}>
				% of bookings cancelled out of total in each period.
				Honours the active office/category filters.
			</Typography>
		</Paper>
	)
}
